import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { useLocation } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { INTEGRATION_EVENT_LABEL, INTEGRATION_EVENTS, type IntegrationEvent, type IntegrationKind } from "@metrickle/schema/constants";
import { api, useApiMutation, useApp } from "../lib/api";
import { useCan } from "../lib/access";
import { fmtAgo } from "../lib/format";
import { track } from "../lib/track";
import { AppHeader, useAppId } from "../components/app-header";
import { TrackWork } from "../components/issues";
import { HelpdeskSection } from "../components/helpdesk";
import { A11yScansSection } from "../components/a11y-scans";
import { ReleasesSection } from "../components/releases";
import { EventSourcesSection } from "../components/event-sources";
import { RevenueSection } from "../components/revenue-sources";
import { FigmaSection } from "../components/designs";
import { ActionMenu, Badge, Button, Callout, Card, CardHeader, CardSection, Checkbox, CodeBlock, ErrorState, Field, Input, Modal, OneTimeSecret, SelectField, Table, TableScroll, notifyError, toaster, useConfirm, Skeleton, type ActionMenuItem } from "../components/ui";

interface Integration {
  id: string;
  kind: IntegrationKind;
  name: string;
  url: string;
  events: IntegrationEvent[];
  enabled: boolean;
  signed: boolean;
  createdAt: number;
  lastDelivery: { ok: boolean; status: number; at: number } | null;
}

interface DeliveryResult {
  ok: boolean;
  status: number;
  attempts: number;
  error: string | null;
}

interface DeliveryLog extends DeliveryResult {
  id: string;
  event: string;
  at: number;
}

const KIND: Record<IntegrationKind, { label: string; url: string; hint: string }> = {
  slack: {
    label: "Slack",
    url: "https://hooks.slack.com/services/…",
    hint: "In Slack: Apps → Incoming Webhooks → Add to Slack, pick a channel, then copy the webhook URL.",
  },
  teams: {
    label: "Microsoft Teams",
    url: "https://….logic.azure.com/workflows/…",
    hint: "In Teams: open the channel's … menu → Workflows → “Post to a channel when a webhook request is received”, then copy the URL.",
  },
  webhook: {
    label: "Webhook",
    url: "https://example.com/hooks/metrickle",
    hint: "We POST JSON, signed with a secret you'll see once. Any 2xx response counts as delivered; 5xx and timeouts are retried twice.",
  },
};

const DOCS = "https://metrickle.com/features/integrations";

const VERIFY_SNIPPET = (secret: string) => `// Verify the Metrickle-Signature header: t=<ms>,v1=<hex HMAC-SHA256 of "<t>.<raw body>">
import { createHmac, timingSafeEqual } from "node:crypto";

export function verify(rawBody: string, header: string, secret = "${secret}") {
  const { t, v1 } = Object.fromEntries(header.split(",").map((p) => p.split("=")));
  if (Math.abs(Date.now() - Number(t)) > 5 * 60_000) return false; // reject replays
  const expected = createHmac("sha256", secret).update(\`\${t}.\${rawBody}\`).digest("hex");
  return typeof v1 === "string" && v1.length === expected.length && timingSafeEqual(Buffer.from(expected), Buffer.from(v1));
}`;

function deliveryStatus(i: Integration): { text: string; tone: "good" | "bad" | "neutral" } {
  if (!i.enabled) return { text: "Off", tone: "neutral" };
  const d = i.lastDelivery;
  if (!d) return { text: "Nothing sent yet", tone: "neutral" };
  return d.ok ? { text: `Delivered ${fmtAgo(d.at)}`, tone: "good" } : { text: `Failing (${d.status || "no response"})`, tone: "bad" };
}

/** Short names for a destination's events, for one-line rows. */
const SHORT: Record<IntegrationEvent, string> = {
  "feedback.created": "Feedback",
  "alert.release_dropped": "Release drops",
  "release.verdict": "Release verdicts",
  "alert.task_success_drop": "Task drops",
  "alert.new_hotspot": "Hotspots",
  "alert.contract_broken": "Protected task gaps",
  "case.opened": "Cases opened",
  "case.closed": "Cases closed",
  "issue.measured": "Fix results",
  "study.booked": "Study bookings",
  "study.completed": "Study completions",
};
const eventSummary = (events: IntegrationEvent[]) => (events.length === INTEGRATION_EVENTS.length ? "Everything" : events.map((e) => SHORT[e]).join(", "));

/** A section of the page: a card with a heading, one line on what it's for, and its content (a ruled `CardSection`). */
function Section({ id, title, description, action, children }: { id: string; title: string; description: string; action?: ReactNode; children: ReactNode }) {
  return (
    <Card id={id} aria-labelledby={`${id}-title`} data-tour={`integrations-${id}`} className="scroll-mt-4">
      <CardHeader title={<span id={`${id}-title`}>{title}</span>} subtitle={description} actions={action} />
      {children}
    </Card>
  );
}

type Dialog = { kind: "add" } | { kind: "log"; i: Integration } | { kind: "events"; i: Integration } | { kind: "secret"; secret: string } | null;

export function Integrations() {
  const appId = useAppId();
  const can = useCan(appId);
  const app = useApp(appId);
  const list = useQuery({ queryKey: ["integrations", appId], queryFn: () => api<Integration[]>(`/apps/${appId}/integrations`) });
  const [dialog, setDialog] = useState<Dialog>(null);
  const close = () => setDialog(null);
  const { hash } = useLocation();

  // Deep links from elsewhere (e.g. "Manage releases" on Overview) land on their section.
  useEffect(() => {
    if (hash && list.data) document.getElementById(hash.slice(1))?.scrollIntoView();
  }, [hash, list.data]);

  const failing = (list.data ?? []).filter((i) => i.enabled && i.lastDelivery && !i.lastDelivery.ok);

  return (
    <>
      <AppHeader
        title="Integrations"
        subtitle={
          <>
            Where events come from, where feedback and alerts go, where issues are created, where revenue comes from, which support tools feed the inbox, and how releases are marked.{" "}
            <a className="text-accent-text underline underline-offset-2" href={`${DOCS}`} target="_blank" rel="noreferrer">
              How integrations work<span className="sr-only"> (opens in a new tab)</span>
            </a>
          </>
        }
      />
      <div className="max-w-4xl space-y-4">
        {failing.length > 0 && (
          <Callout tone="bad" title={failing.length === 1 ? "A destination is failing" : `${failing.length} destinations are failing`}>
            <ul className="mt-1 space-y-0.5 text-ink-2">
              {failing.map((i) => (
                <li key={i.id}>
                  {i.name}: {i.lastDelivery!.status ? `HTTP ${i.lastDelivery!.status}` : "no response"} {fmtAgo(i.lastDelivery!.at)}.{" "}
                  <Button type="button" variant="link" className="underline" onClick={() => setDialog({ kind: "log", i })}>
                    View log
                  </Button>
                </li>
              ))}
            </ul>
          </Callout>
        )}

        <Section
          id="notify"
          title="Notify"
          description="Where feedback reports, alerts and fix results are sent."
          action={
            can("integrations.manage") && (
              <Button size="sm" onClick={() => setDialog({ kind: "add" })}>
                <Plus size={13} aria-hidden /> Add Slack, Teams or webhook
              </Button>
            )
          }
        >
          <CardSection flush>
            {list.data?.length ? (
              <ul className="divide-y divide-line">
                {list.data.map((i) => (
                  <NotifyRow key={i.id} appId={appId} i={i} canManage={can("integrations.manage")} onDialog={setDialog} />
                ))}
              </ul>
            ) : (
              <p className="px-4 py-3 text-sm text-ink-2">
                Nothing set up. Send accessibility barrier reports, task success drops and new friction hotspots to a Slack or Teams channel as they happen.
              </p>
            )}
          </CardSection>
        </Section>

        <Section id="sources" title="Event sources" description="Send events from Segment or RudderStack, without adding Metrickle's script.">
          <EventSourcesSection appId={appId} />
        </Section>

        <Section id="track" title="Track work" description="Where “Create issue” sends feedback reports, hotspots and tasks.">
          <TrackWork appId={appId} />
        </Section>

        <Section id="figma" title="Figma" description="Tag Figma frames with the screen they draw, and see each screen's release evidence. Shared by every app in this workspace.">
          {app.data ? <FigmaSection orgId={app.data.organizationId} canConnect={can("designs.connect")} /> : <Skeleton className="m-4 h-12" />}
        </Section>

        <Section id="revenue" title="Revenue" description="Payments, refunds and cancels from Stripe or RevenueCat, on the person who made them.">
          <RevenueSection appId={appId} />
        </Section>

        <Section id="support" title="Support inbox" description="Zendesk tickets and Intercom conversations that come into Feedback.">
          <HelpdeskSection appId={appId} />
        </Section>

        <Section id="releases" title="Releases" description="How deploys are marked on the Overview chart and in alerts.">
          <ReleasesSection appId={appId} />
        </Section>

        <Section id="a11y-scans" title="Accessibility scans" description="axe-core results from CI, shown next to the friction they cause.">
          <A11yScansSection appId={appId} />
        </Section>
      </div>

      <Modal open={dialog?.kind === "add"} onOpenChange={(o) => !o && close()} title="Add a destination" description="Slack, a Microsoft Teams channel or your own endpoint.">
        {dialog?.kind === "add" && <AddDestination appId={appId} onDone={(secret) => setDialog(secret ? { kind: "secret", secret } : null)} />}
      </Modal>
      <Modal open={dialog?.kind === "events"} onOpenChange={(o) => !o && close()} title={dialog?.kind === "events" ? `What goes to ${dialog.i.name}` : ""}>
        {dialog?.kind === "events" && <EditEvents appId={appId} i={dialog.i} onDone={close} />}
      </Modal>
      <Modal open={dialog?.kind === "log"} onOpenChange={(o) => !o && close()} title={dialog?.kind === "log" ? `Delivery log: ${dialog.i.name}` : ""} description="The last 50 deliveries, newest first." wide>
        {dialog?.kind === "log" && <DeliveryLogTable appId={appId} integrationId={dialog.i.id} />}
      </Modal>
      <Modal
        open={dialog?.kind === "secret"}
        onOpenChange={(o) => !o && close()}
        title="Webhook added"
        description="Store this signing secret with your endpoint and check every request's Metrickle-Signature header."
        wide
      >
        {dialog?.kind === "secret" && (
          <OneTimeSecret bare title="Signing secret" secret={dialog.secret} label="Signing secret" onDone={close}>
            <CodeBlock code={VERIFY_SNIPPET(dialog.secret)} label="Verify a request (Node)" language="typescript" />
            <p className="text-xs text-ink-3">
              Payload format, headers and every event type:{" "}
              <a className="text-accent-text underline underline-offset-2" href="https://metrickle.com/developers/webhooks" target="_blank" rel="noreferrer">
                webhook reference<span className="sr-only"> (opens in a new tab)</span>
              </a>
              .
            </p>
          </OneTimeSecret>
        )}
      </Modal>
    </>
  );
}

function NotifyRow({ appId, i, canManage, onDialog }: { appId: string; i: Integration; canManage: boolean; onDialog: (d: Dialog) => void }) {
  const status = deliveryStatus(i);
  const invalidate = [["integrations", appId]];
  const update = useApiMutation((b: { enabled: boolean }) => api(`/apps/${appId}/integrations/${i.id}`, { method: "PATCH", json: b }), invalidate);
  const remove = useApiMutation(() => api(`/apps/${appId}/integrations/${i.id}`, { method: "DELETE" }), invalidate);
  const test = useApiMutation(() => api<DeliveryResult>(`/apps/${appId}/integrations/${i.id}/test`, { method: "POST" }), invalidate);
  const { confirm, dialog } = useConfirm();

  const sendTest = () =>
    test.mutate(undefined, {
      onSuccess: (d) => {
        track("integration_tested", { kind: i.kind, ok: d.ok });
        if (d.ok) toaster.success({ title: "Test delivered", description: `${i.name} answered ${d.status}.` });
        else toaster.error({ title: "Test failed", description: `${i.name}: ${d.status ? `HTTP ${d.status}` : "no response"}${d.error ? ` — ${d.error}` : ""}` });
      },
      onError: notifyError,
    });

  const actions: ActionMenuItem[] = [
    ...(canManage ? [{ value: "test", label: "Send test", onSelect: sendTest }] : []),
    { value: "log", label: "Delivery log", onSelect: () => onDialog({ kind: "log", i }) },
    ...(canManage
      ? [
          { value: "events", label: "Choose what's sent", onSelect: () => onDialog({ kind: "events", i }) },
          { value: "toggle", label: i.enabled ? "Turn off" : "Turn on", onSelect: () => update.mutate({ enabled: !i.enabled }, { onSuccess: () => toaster.success({ title: i.enabled ? `${i.name} turned off` : `${i.name} turned on` }), onError: notifyError }) },
          {
            value: "remove",
            label: "Remove",
            danger: true,
            onSelect: async () => {
              if (await confirm({ title: `Remove “${i.name}”?`, description: "Nothing more is sent there. Its delivery log is deleted too.", confirmLabel: "Remove" }))
                remove.mutate(undefined, {
                  onSuccess: () => {
                    track("integration_deleted", { kind: i.kind });
                    toaster.success({ title: `${i.name} removed` });
                  },
                  onError: notifyError,
                });
            },
          },
        ]
      : []),
  ];

  return (
    <li className="grid grid-cols-[1fr_auto] items-center gap-x-3 gap-y-1 px-4 py-2.5 @xl:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_auto_auto]">
      <div className="min-w-0">
        <div className="truncate text-sm font-medium text-ink">{i.name}</div>
        <div className="truncate text-xs text-ink-3">
          {KIND[i.kind].label} · <span className="font-mono">{i.url}</span>
        </div>
      </div>
      <div className="col-start-1 row-start-2 truncate text-xs text-ink-2 @xl:col-start-auto @xl:row-start-auto">{eventSummary(i.events)}</div>
      <div className="col-start-1 row-start-3 @xl:col-start-auto @xl:row-start-auto">
        <Badge tone={status.tone}>{status.text}</Badge>
      </div>
      <ActionMenu
        label={`Actions for ${i.name}`}
        items={actions}
        triggerClassName="col-start-2 row-span-3 row-start-1 @xl:col-start-auto @xl:row-span-1 @xl:row-start-auto"
      />
      {dialog}
    </li>
  );
}

function EventChoices({ value, onChange }: { value: IntegrationEvent[]; onChange: (v: IntegrationEvent[]) => void }) {
  return (
    <fieldset className="space-y-1.5">
      <legend className="text-sm text-ink">Send</legend>
      {INTEGRATION_EVENTS.map((ev) => (
        <Checkbox
          key={ev}
          checked={value.includes(ev)}
          hint={INTEGRATION_EVENT_LABEL[ev].hint}
          onCheckedChange={(on) => onChange(INTEGRATION_EVENTS.filter((x) => (x === ev ? on : value.includes(x))))}
        >
          {INTEGRATION_EVENT_LABEL[ev].label}
        </Checkbox>
      ))}
    </fieldset>
  );
}

function AddDestination({ appId, onDone }: { appId: string; onDone: (secret?: string) => void }) {
  const [kind, setKind] = useState<IntegrationKind>("slack");
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [events, setEvents] = useState<IntegrationEvent[]>([...INTEGRATION_EVENTS]);
  const create = useApiMutation(
    (b: { kind: IntegrationKind; name: string; url: string; events: IntegrationEvent[] }) => api<Integration & { secret: string | null }>(`/apps/${appId}/integrations`, { method: "POST", json: b }),
    [["integrations", appId]],
  );
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      const created = await create.mutateAsync({ kind, name: name.trim() || KIND[kind].label, url: url.trim(), events });
      track("integration_created", { kind, events: events.length });
      if (!created.secret) toaster.success({ title: `${created.name} added`, description: "Use “Send test” in its menu to check it's connected." });
      onDone(created.secret ?? undefined);
    } catch (err) {
      notifyError(err);
    }
  };
  return (
    <form onSubmit={submit} className="space-y-3">
      <Field label="Type">
        <SelectField className="w-full" items={(Object.keys(KIND) as IntegrationKind[]).map((k) => ({ value: k, label: KIND[k].label }))} value={kind} onChange={(v) => setKind(v as IntegrationKind)} />
      </Field>
      <Field label="URL" hint={KIND[kind].hint}>
        <Input required type="url" inputMode="url" autoComplete="off" maxLength={2048} className="font-mono" placeholder={KIND[kind].url} value={url} onChange={(e) => setUrl(e.target.value)} />
      </Field>
      <Field label="Name">
        <Input maxLength={80} placeholder={kind === "webhook" ? "Ops endpoint" : "#product-feedback"} value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      <EventChoices value={events} onChange={setEvents} />
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" onClick={() => onDone()}>Cancel</Button>
        <Button variant="primary" disabled={!events.length} pending={create.isPending} pendingLabel="Adding…">
          Add destination
        </Button>
      </div>
    </form>
  );
}

function EditEvents({ appId, i, onDone }: { appId: string; i: Integration; onDone: () => void }) {
  const [events, setEvents] = useState<IntegrationEvent[]>(i.events);
  const update = useApiMutation((b: { events: IntegrationEvent[] }) => api(`/apps/${appId}/integrations/${i.id}`, { method: "PATCH", json: b }), [["integrations", appId]]);
  return (
    <form
      className="space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        update.mutate(
          { events },
          {
            onSuccess: () => {
              toaster.success({ title: "Events saved" });
              onDone();
            },
            onError: notifyError,
          },
        );
      }}
    >
      <EventChoices value={events} onChange={setEvents} />
      {!events.length && <p className="text-xs text-ink-3">Choose at least one. To pause this destination, use “Turn off” instead.</p>}
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" onClick={onDone}>Cancel</Button>
        <Button variant="primary" disabled={!events.length} pending={update.isPending} pendingLabel="Saving…">
          Save
        </Button>
      </div>
    </form>
  );
}

function DeliveryLogTable({ appId, integrationId }: { appId: string; integrationId: string }) {
  const log = useQuery({ queryKey: ["integration-log", appId, integrationId], queryFn: () => api<DeliveryLog[]>(`/apps/${appId}/integrations/${integrationId}/deliveries`) });
  if (log.isError) return <ErrorState title="Couldn't load the delivery log" error={log.error} />;
  if (!log.data)
    return (
      <div aria-busy="true">
        <Skeleton className="h-32" />
        <span className="sr-only">Loading…</span>
      </div>
    );
  if (!log.data.length) return <p className="text-sm text-ink-2">Nothing has been sent yet. Use “Send test” to try it.</p>;
  return (
    <TableScroll label="Recent deliveries" className="max-h-96 overflow-y-auto">
      <Table caption="Recent deliveries" flush>
        <thead>
          <tr>
            <th scope="col" className="py-1.5">When</th>
            <th scope="col" className="py-1.5">Event</th>
            <th scope="col" className="py-1.5">Result</th>
          </tr>
        </thead>
        <tbody>
          {log.data.map((d) => (
            <tr key={d.id}>
              <td className="py-1.5 whitespace-nowrap text-ink-2">{fmtAgo(d.at)}</td>
              <td className="py-1.5 font-mono text-xs text-ink">{d.event}</td>
              <td className="py-1.5">
                <Badge tone={d.ok ? "good" : "bad"}>{d.ok ? `Delivered (${d.status})` : `Failed (${d.status || "no response"})`}</Badge>
                {d.attempts > 1 && <span className="ml-2 text-xs text-ink-3">{d.attempts} attempts</span>}
                {!d.ok && d.error && <span className="mt-0.5 block text-xs break-all text-ink-3">{d.error}</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </TableScroll>
  );
}
